import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Button from "@material-ui/core/Button";
import Fade from "@material-ui/core/Fade";
import Modal from "@material-ui/core/Modal";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import TagComponent from "@meetyoo/components/src/components/Tag";
import { changeTicketTags, toggleTagModal } from "../../../../../actions/projectmail";

import { defaultPasscodeTheme } from "../../../../../constants/passCodeUtils";
import { spliceTag, originTag } from "../../../../../utils/objectMethods/tagOperations";

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  tag: {
    cursor: "pointer",
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContainer: {
    width: "750px",
    backgroundColor: "#eee",
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  tagTitle: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  inline: {
    display: "flex",
    maxWidth: "100%",
    flexWrap: "wrap",
  },
  container: { padding: theme.spacing(2, 2, 2, 2), minHeight: "96px" },
  tagWrapper: {
    padding: theme.spacing(1, 1, 1, 1),
  },
  bottomPadding: {
    paddingBottom: theme.spacing(1),
  },
  title: {
    fontSize: "16px",
    fontWeight: "bold",
  },
  paddingLeft: {
    paddingLeft: theme.spacing(2),
  },
}));

export const TagModalComponent = ({ changeTicketTagsFn, colorMap, close, rowId, tags, open, translate }) => {
  const classes = useStyles();
  const [addTag, setAddTag] = useState([]);
  const [removeTag, setRemoveTag] = useState([]);
  const [convertedTags, setConvertedTags] = useState([]);

  useEffect(() => {
    if (convertedTags.length === 0) {
      setConvertedTags(
        Object.keys(colorMap).map(tag => {
          return originTag(tag);
        })
      );
    }
  }, [convertedTags, colorMap, setConvertedTags, setAddTag, setRemoveTag]);

  const modifiedTag = (addableTag, setAddableTag, removeableTag, setRemoveableTag, tag) => {
    return (
      <div
        aria-hidden
        key={spliceTag(tag)}
        role="button"
        className={classes.tag}
        onClick={() => {
          if (addableTag.indexOf(tag) === -1) setAddableTag(addableTag.concat([tag]));
          if (removeableTag.indexOf(tag) > -1) {
            removeableTag.splice(removeableTag.indexOf(tag), 1);
            setRemoveableTag(removeableTag);
          }
        }}
      >
        {[tag].map(() => {
          if (tag.charAt(0) === "#") {
            return <TagComponent key={tag} size="big" text={spliceTag(tag)} color={colorMap[spliceTag(tag)]} />;
          }
          return <TagComponent key={tag} size="big" text={tag} color={colorMap[tag]} />;
        })}
      </div>
    );
  };

  return (
    open === true && (
      <Modal
        open={open}
        className={classes.modal}
        onClose={() => {
          close();
          setAddTag([]);
          setRemoveTag([]);
        }}
      >
        <Fade in={open}>
          <div key="modalContainer" className={classes.modalContainer}>
            <div className={classes.tagTitle}>{translate.tags}</div>
            <div className={classes.bottomPadding} />
            <Paper>
              <div className={classes.container}>
                <div className={`${classes.inline}`}>
                  <AddCircleIcon style={{ color: "rgb(0, 255, 0)" }} />
                  <div className={`${classes.bottomPadding} ${classes.title}`}>{translate.assigned}</div>
                </div>
                <div className={classes.inline}>
                  {tags.map(tag => {
                    if (addTag.indexOf(tag) === -1 && removeTag.indexOf(tag) === -1)
                      return modifiedTag(removeTag, setRemoveTag, addTag, setAddTag, tag);
                    return null;
                  })}
                  {addTag.map(tag => modifiedTag(removeTag, setRemoveTag, addTag, setAddTag, tag))}
                </div>
              </div>
            </Paper>
            <div className={classes.bottomPadding} />
            <Paper>
              <div className={classes.container}>
                <div className={`${classes.inline}`}>
                  <RemoveCircleIcon style={{ color: "rgb(255, 0, 0)" }} />
                  <div className={`${classes.bottomPadding} ${classes.title}`}>{translate.projectmail_add}</div>
                </div>
                <div className={classes.inline}>
                  {convertedTags.map(tag => {
                    if (tags.indexOf(tag) === -1 && addTag.indexOf(tag) === -1)
                      return modifiedTag(addTag, setAddTag, removeTag, setRemoveTag, tag);
                    return null;
                  })}
                  {convertedTags.map(tag => {
                    if (removeTag.indexOf(tag) > -1 && tags.indexOf(tag) > -1)
                      return modifiedTag(addTag, setAddTag, removeTag, setRemoveTag, tag);
                    return null;
                  })}
                </div>
              </div>
            </Paper>
            <div className={`${classes.bottomPadding} ${classes.lineBreak}`} />
            <div className={classes.inline}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  close();
                  setAddTag([]);
                  setRemoveTag([]);
                }}
              >
                {translate.backButton}
              </Button>
              <div className={classes.paddingLeft}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const remove = removeTag.filter(tag => tags.indexOf(tag) !== -1);
                    if (remove.length !== 0) {
                      await changeTicketTagsFn(rowId, remove, true);
                    }
                    const add = addTag.filter(tag => tags.indexOf(tag) === -1);
                    if (add.length !== 0) {
                      await changeTicketTagsFn(rowId, add, false);
                    }
                    close();
                    setAddTag([]);
                    setRemoveTag([]);
                  }}
                >
                  {translate.saveButton}
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    )
  );
};

TagModalComponent.defaultProps = {
  tags: [],
  rowId: undefined,
};

TagModalComponent.propTypes = {
  changeTicketTagsFn: PropTypes.func.isRequired,
  colorMap: PropTypes.object.isRequired,
  rowId: PropTypes.number,
  close: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  tags: PropTypes.array,
};

const mapDispatchToProps = dispatch => ({
  changeTicketTagsFn: async (id, tags, del) => dispatch(changeTicketTags(id, tags, del)),
  close: () => dispatch(toggleTagModal(false)),
});

const mapStateToProps = state => ({
  colorMap: state.projectmail.colorMap,
  currentType: state.projectmail.currentType,
  translate: state.ui.translate,
  open: state.projectmail.tagModalOpen,
  tags: state.projectmail.currentRow ? state.projectmail.currentRow.tags : undefined,
  rowId: state.projectmail.currentRow ? state.projectmail.currentRow.id : undefined,
});

const TagModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(TagModalComponent);

export default TagModal;
